/**
 * CollectCard — the transcript's side of an assistant message that carries a
 * ``collect`` surface (composer-as-forms): the AI's question, with the same
 * avatar + bubble anatomy as DecisionCard. An attach card (F19) asks from its
 * typed items.
 *
 * The card never draws an intake. The upload and link intake lives in the
 * composer (`ComposerCollectIntake`), which hosts it while the card waits on
 * its answer and nothing runs, and what the user sent shows as their bubble.
 * Before QA4 (PR N4) the card drew a second, generic intake of its own
 * ("Drag & drop or click to upload", "Paste a landing-page URL…", "Add to
 * study") whenever the composer did not host it: while the turn that carried
 * the card was still settling, that box flashed for about 2 s before the
 * composer's attach card replaced it.
 */

import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import { ChatMessageCollect } from "../types";
import { attachCardQuestion } from "../utils/attachCardCopy";
import {
  AssistantReplyMessage,
  assistantReplyLines,
  ReplyLines,
} from "./AssistantReplyLines";

interface CollectCardProps {
  /** AI avatar image src — matches the icon used by ChatBubble. */
  aiIconSrc: string;
  /** The study's asset panel heading, for the reply's not-done lines (F16). */
  assetPanelHeading: string;
  message: AssistantReplyMessage & { collect: ChatMessageCollect };
  /** The opening-chain step that asked this card, from its record. */
  chainCode?: string | null;
}

export function CollectCard(props: CollectCardProps) {
  const { aiIconSrc, assetPanelHeading, message, chainCode } = props;
  const { t } = useTranslation("app");
  const { collect } = message;
  const lines = assistantReplyLines(message, chainCode, assetPanelHeading, t);
  // An attach card asks from its typed items (F19); an optional card a model
  // asked for new files with says it may be passed.
  const question = collect.attachTo
    ? attachCardQuestion(collect.attachTo, t)
    : collect.optional
      ? t("questionnaire.attachCard.askNew")
      : "";
  // The question follows the reply's sentence, or, when the reply lists
  // parts, comes after the list as its own line.
  const text = (
    lines.length > 1
      ? [...lines, question]
      : [[...lines, question].filter(Boolean).join(" ")]
  )
    .filter(Boolean)
    .join("\n");

  return (
    <div className="flex gap-2 flex-row pr-[60px]">
      {/* AI avatar — identical to ChatBubble/DecisionCard */}
      <div className="h-7 w-7 flex-shrink-0 overflow-hidden rounded-full bg-study-chat-ai-bubble flex items-center justify-center">
        <img
          src={aiIconSrc}
          alt="AI"
          className="h-full w-full object-contain"
          style={{ transform: "scale(1.3)" }}
        />
      </div>

      <div className="flex-1 min-w-0 text-left">
        <div
          data-testid="collect-card"
          className={cn(
            "rounded-2xl rounded-tl-sm px-4 pt-3 pb-3 text-base leading-[1.6]",
            "bg-study-chat-ai-bubble text-foreground",
          )}
        >
          <ReplyLines id={message.id} text={text} />
        </div>
      </div>
    </div>
  );
}
